import React, { useState } from 'react';
import './ImportExportModal.css';

interface ImportExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (teamData: string) => void;
  onExport: () => string;
  onTeamSaved?: () => void; // Callback when a team is saved
}

const ImportExportModal: React.FC<ImportExportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  onExport,
  onTeamSaved
}) => {
  const [activeTab, setActiveTab] = useState<'import' | 'export' | 'saved'>('import');
  const [importText, setImportText] = useState('');
  const [exportText, setExportText] = useState('');
  const [savedTeams, setSavedTeams] = useState<Array<{ name: string; date: string }>>([]);
  const [teamName, setTeamName] = useState('');
  const [isPersisted, setIsPersisted] = useState<boolean | null>(null);

  // Request persistent storage to reduce chance of data loss
  React.useEffect(() => {
    if ('storage' in navigator && 'persist' in navigator.storage) {
      navigator.storage.persisted().then(persisted => {
        setIsPersisted(persisted);
        if (!persisted) {
          navigator.storage.persist().then(granted => {
            setIsPersisted(granted);
          });
        }
      });
    }
  }, []);

  React.useEffect(() => {
    if (isOpen && activeTab === 'export') {
      setExportText(onExport());
    }
    if (isOpen && activeTab === 'saved') {
      loadSavedTeams();
    }
  }, [isOpen, activeTab, onExport]);

  const loadSavedTeams = () => {
    const teams = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('pokemon-team-')) {
        const teamData = localStorage.getItem(key);
        if (teamData) {
          try {
            const parsed = JSON.parse(teamData);
            teams.push({
              name: parsed.name || key.replace('pokemon-team-', ''),
              date: parsed.date || 'Unknown'
            });
          } catch (e) {
            console.error('Error parsing team:', e);
          }
        }
      }
    }
    setSavedTeams(teams);
  };

  const handleImport = () => {
    if (importText.trim()) {
      try {
        // Ask if user wants to save the imported team
        const shouldSave = window.confirm('Do you want to save this imported team to your Custom sets?');

        if (shouldSave) {
          const name = window.prompt('Enter a name for this team:', 'Imported Team');
          if (name && name.trim()) {
            const teamData = {
              name: name.trim(),
              date: new Date().toISOString(),
              data: importText
            };
            const key = `pokemon-team-${name.trim()}`;
            console.log('Saving team to localStorage:', key, teamData);
            localStorage.setItem(key, JSON.stringify(teamData));
            console.log('Saved. Verifying:', localStorage.getItem(key));
            loadSavedTeams();
            if (onTeamSaved) {
              console.log('Calling onTeamSaved callback');
              onTeamSaved();
            }
          }
        }

        onImport(importText);
        setImportText('');
        onClose();
      } catch (error) {
        alert('Error importing team: ' + (error as Error).message);
      }
    }
  };

  const handleSaveTeam = () => {
    if (teamName.trim()) {
      const teamData = {
        name: teamName,
        date: new Date().toISOString(),
        data: onExport()
      };
      localStorage.setItem(`pokemon-team-${teamName}`, JSON.stringify(teamData));
      setTeamName('');
      loadSavedTeams();
      alert('Team saved successfully!');
      // Notify parent to refresh sets
      if (onTeamSaved) onTeamSaved();
    } else {
      alert('Please enter a team name');
    }
  };

  const handleLoadTeam = (teamName: string) => {
    const teamData = localStorage.getItem(`pokemon-team-${teamName}`);
    if (teamData) {
      try {
        const parsed = JSON.parse(teamData);
        onImport(parsed.data);
        onClose();
      } catch (error) {
        alert('Error loading team: ' + (error as Error).message);
      }
    }
  };

  const handleDeleteTeam = (teamName: string) => {
    if (window.confirm(`Are you sure you want to delete "${teamName}"?`)) {
      localStorage.removeItem(`pokemon-team-${teamName}`);
      loadSavedTeams();
      // Notify parent to refresh sets
      if (onTeamSaved) onTeamSaved();
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(exportText);
    alert('Copied to clipboard!');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Import / Export Teams</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === 'import' ? 'active' : ''}`}
            onClick={() => setActiveTab('import')}
          >
            Import
          </button>
          <button
            className={`tab-button ${activeTab === 'export' ? 'active' : ''}`}
            onClick={() => setActiveTab('export')}
          >
            Export
          </button>
          <button
            className={`tab-button ${activeTab === 'saved' ? 'active' : ''}`}
            onClick={() => setActiveTab('saved')}
          >
            Saved Teams
          </button>
        </div>

        <div className="modal-body">
          {activeTab === 'import' && (
            <div className="import-section">
              <p className="section-description">
                Paste your team in Pokémon Showdown format below:
              </p>
              <textarea
                className="import-textarea"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="Talonflame @ Flyinium Z&#10;Ability: Gale Wings&#10;EVs: 252 Atk / 4 Def / 252 Spe&#10;Jolly Nature&#10;- Brave Bird&#10;- Flare Blitz&#10;- Swords Dance&#10;- Roost"
              />
              <button className="primary-button" onClick={handleImport}>
                Import Team
              </button>
            </div>
          )}

          {activeTab === 'export' && (
            <div className="export-section">
              <p className="section-description">
                Copy this text to share your team:
              </p>
              <textarea
                className="export-textarea"
                value={exportText}
                readOnly
              />
              <div className="export-actions">
                <button className="primary-button" onClick={handleCopyToClipboard}>
                  Copy to Clipboard
                </button>
                <div className="save-team-row">
                  <input
                    type="text"
                    className="team-name-input"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="Team name..."
                  />
                  <button className="secondary-button" onClick={handleSaveTeam}>
                    Save Team
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'saved' && (
            <div className="saved-section">
              <div className="storage-warning">
                <p>
                  <strong>⚠️ Teams are stored in your browser's localStorage.</strong>
                </p>
                <p style={{ fontSize: '13px', marginTop: '8px' }}>
                  Your teams may be lost if you clear browser data/cookies, use cleaning tools like CCleaner,
                  or switch browsers. We recommend regularly backing up important teams using the Export tab.
                </p>
                {isPersisted === false && (
                  <p style={{ fontSize: '12px', marginTop: '8px', color: '#ff9800' }}>
                    Note: Storage persistence not granted. Your browser may delete this data more aggressively.
                  </p>
                )}
              </div>
              <p className="section-description">
                Your saved teams:
              </p>
              {savedTeams.length === 0 ? (
                <p className="no-teams">No saved teams yet. Export and save a team to get started!</p>
              ) : (
                <div className="teams-list">
                  {savedTeams.map((team) => (
                    <div key={team.name} className="team-item">
                      <div className="team-info">
                        <span className="team-name">{team.name}</span>
                        <span className="team-date">{new Date(team.date).toLocaleDateString()}</span>
                      </div>
                      <div className="team-actions">
                        <button
                          className="load-button"
                          onClick={() => handleLoadTeam(team.name)}
                        >
                          Load
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDeleteTeam(team.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportExportModal;
